import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { navigateToSection } from "../utils/navigation";

const Header = () => {
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleSectionNavigation = (sectionId) => {
    navigateToSection(sectionId);
    closeMenu();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="p-6">
      <nav className="flex items-center justify-between container mx-auto">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center space-x-0"
          onClick={closeMenu}
        >
          <img
            src="/test-omega-logo.png"
            alt="Test Omega Logo"
            className="h-24 w-24 object-contain drop-shadow-lg"
          />
          <div className="text-4xl font-bold text-white">
            Test<span className="text-gradient">Omega</span>
          </div>
        </Link>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center space-x-4">
          {/* Show Home button only when not on home page */}
          {!isHomePage && (
            <Link
              to="/"
              onClick={closeMenu}
              className="px-3 py-2 text-gray-400 hover:text-white transition"
            >
              Home
            </Link>
          )}

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-md focus:outline-none"
            aria-label="Toggle theme"
          >
            {isDark ? (
              <svg
                className="w-6 h-6 text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>

          {/* Desktop Menu Button */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={toggleMenu}
              className="flex items-center px-4 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800 focus:outline-none"
              aria-label="Toggle menu"
            >
              <span className="mr-2">Menu</span>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                )}
              </svg>
            </button>

            {/* Desktop Dropdown Menu */}
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-gray-900 rounded-lg border border-gray-700 shadow-xl z-50">
                <div className="p-4">
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => handleSectionNavigation("services")}
                      className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
                    >
                      � Our Services
                    </button>
                    <button
                      onClick={() => handleSectionNavigation("technology")}
                      className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
                    >
                      �️ Technology Stack
                    </button>
                    <button
                      onClick={() => handleSectionNavigation("case-studies")}
                      className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
                    >
                      📈 Case Studies
                    </button>
                    <Link
                      to="/ai-test-generation"
                      onClick={closeMenu}
                      className="px-3 py-2 text-gradient hover:text-blue-300 transition rounded-md hover:bg-gray-800 font-medium"
                    >
                      🤖 AI QA Agent
                    </Link>
                    <button
                      onClick={() => handleSectionNavigation("resources")}
                      className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
                    >
                      📚 Resources
                    </button>
                    <Link
                      to="/educational-topics"
                      onClick={closeMenu}
                      className="px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
                    >
                      🎓 Educational Topics
                    </Link>
                    <Link
                      to="/open-source-tools"
                      onClick={closeMenu}
                      className="px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
                    >
                      🔧 Tools
                    </Link>
                    <hr className="border-gray-700 my-2" />
                    <button
                      onClick={() => handleSectionNavigation("services")}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-center"
                    >
                      🎯 Start Consultation
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Actions */}
        <div className="flex md:hidden items-center space-x-2">
          {/* Show Home button only when not on home page */}
          {!isHomePage && (
            <Link
              to="/"
              onClick={closeMenu}
              className="px-3 py-2 text-gray-400 hover:text-white transition text-sm"
            >
              Home
            </Link>
          )}

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-md focus:outline-none"
            aria-label="Toggle theme"
          >
            {isDark ? (
              <svg
                className="w-5 h-5 text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>

          {/* Hamburger Menu Button */}
          <button
            onClick={toggleMenu}
            className="p-2 rounded-md focus:outline-none"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden mt-4 bg-gray-900 rounded-lg border border-gray-700 p-4">
          <div className="flex flex-col space-y-3">
            <button
              onClick={() => handleSectionNavigation("services")}
              className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
            >
              � Our Services
            </button>
            <button
              onClick={() => handleSectionNavigation("technology")}
              className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
            >
              �️ Technology Stack
            </button>
            <button
              onClick={() => handleSectionNavigation("case-studies")}
              className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
            >
              📈 Case Studies
            </button>
            <Link
              to="/ai-test-generation"
              onClick={closeMenu}
              className="px-3 py-2 text-gradient hover:text-blue-300 transition rounded-md hover:bg-gray-800 font-medium"
            >
              🤖 AI QA Agent
            </Link>
            <button
              onClick={() => handleSectionNavigation("resources")}
              className="text-left px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
            >
              📚 Resources
            </button>
            <Link
              to="/educational-topics"
              onClick={closeMenu}
              className="px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
            >
              🎓 Educational Topics
            </Link>
            <Link
              to="/open-source-tools"
              onClick={closeMenu}
              className="px-3 py-2 text-gray-400 hover:text-white transition rounded-md hover:bg-gray-800"
            >
              🔧 Tools
            </Link>
            <hr className="border-gray-700" />
            <button
              onClick={() => handleSectionNavigation("services")}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-center"
            >
              🎯 Start Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
